/**
 * THE PENDING BLOCK — one rendering, every surface that shows an activity verdict.
 *
 * The validator reports three channels, not two: `errors` blocks, `warnings` advises, and
 * `pending` names a reference that is legal in shape but whose reference data is not committed
 * yet. Pending leaves `ok: true` on purpose — nothing about it stops the draft being proposed —
 * so a render that tests `ok` and lists `errors` shows such a draft as plainly valid and puts the
 * finding nowhere. The operator proposes, and learns what was missing from a CI failure.
 *
 * WHY THE BADGE IS NOT IN HERE. The four renders that show an activity verdict size and colour
 * their badge differently and none of them is wrong, so what is shared is the finding list. Each
 * host keeps its own badge and adds this block beneath it.
 *
 * THE WORDS SAY NOTHING ABOUT THE DRAFT, and that is deliberate. This component sees one list and
 * not the rest of the verdict, so neither "this draft can be proposed" nor "this draft cannot be
 * published" is a claim it can support. A verdict carrying an error AND a pending finding is
 * reachable, and every host correctly refuses that proposal — copy promising otherwise contradicts
 * the disabled button beside it. And the wire's `pending` merges the fleet's other findings in on
 * purpose, so a row here may name a file this turn is not authoring; attributing its prerequisite
 * to the draft being edited names the wrong consumer. Hence the neutral form: these do not block a
 * proposal, and — where the publish gate runs — publishing is refused until each row lands. The
 * qualifier is not hedging: the gate is the canonical validator, invoked by a fleet's own
 * deploy-fleet.yml, and there are paths it does not reach today (SkillStudio does not run it at
 * all, issue #2120). An unqualified "cannot be published" would promise a block the platform does
 * not deliver everywhere.
 *
 * The words are chosen against a specific misreading: an operator who sees "error" edits their
 * draft, and there is nothing in the draft to edit. The reference is correct; the row it names
 * has not been committed. The fix belongs to whoever commits it, which is why the finding text —
 * usually carrying the ticket that tracks the row — is rendered verbatim rather than summarised.
 */

export interface PendingReferencesProps {
  /** The verdict's `pending` findings. Absent and empty both render nothing: a clean draft must
   *  not grow a block about data it does not reference. */
  pending?: string[]
  /** The host's own type scale, for the same reason BreakingConsumerList takes one. */
  className?: string
}

export function PendingReferences({ pending, className }: PendingReferencesProps) {
  if (!pending || pending.length === 0) return null

  return (
    <div className={className ?? 'text-xs'} data-testid="pending-data-references">
      <p className="text-amber-800">
        Waiting on reference data that is not committed yet. Nothing below blocks a proposal by
        itself. Where the publish gate runs, it is refused until each row lands:
      </p>
      <ul className="list-disc list-inside text-amber-800">
        {pending.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </div>
  )
}
